import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Navigation Component for Homepage
const Navigation = () => {
  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50 border-b border-[#E5E0D8]" data-testid="navigation">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 py-4 flex justify-between items-center">
        <a 
          href="#hero" 
          className="font-serif text-lg text-[#1C1C1C] hover:text-[#E68A00] transition-colors"
          data-testid="nav-link-home"
        >
          Sai Acuity
        </a>
        <div className="hidden sm:flex items-center gap-8">
          <a href="#what-i-do" className="text-sm tracking-wide text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors" data-testid="nav-link-conversations">Conversations</a>
          <a href="#what-i-do" className="text-sm tracking-wide text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors" data-testid="nav-link-stories">Stories</a>
          <a href="#what-i-do" className="text-sm tracking-wide text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors" data-testid="nav-link-systems">Systems</a>
          <Link to="/from-intuition-to-intention" className="text-sm tracking-wide text-[#5C5C5C] hover:text-[#E68A00] transition-colors" data-testid="nav-link-experiments">Experiments</Link>
        </div>
      </div>
    </nav>
  );
};

// Section 1: Hero
const HeroSection = () => {
  return (
    <section id="hero" className="min-h-screen flex flex-col justify-center px-6 sm:px-12 pt-20" data-testid="hero-section">
      <AnimatedSection className="max-w-5xl mx-auto w-full">
        <motion.span 
          variants={fadeUp}
          className="text-xs font-sans uppercase tracking-[0.2em] text-[#E68A00] mb-6 block"
        >
          Ravi Jagarlapudi
        </motion.span>
        
        <motion.h1 
          variants={fadeUp}
          className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] text-[#1C1C1C] mb-8 max-w-4xl"
        >
          Some people collect experiences. I turn them into wisdom others can walk away with.
        </motion.h1>
        
        <motion.p 
          variants={fadeUp}
          className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] mb-12"
        >
          Founder, Sai Acuity · Host, The Monday Morning Learning Podcast · Author & Book Compiler
        </motion.p>
        
        <motion.div variants={fadeUp}>
          <a 
            href="mailto:ravi.jagarlapudi@saiacuity.com"
            className="btn-primary inline-block font-sans"
            data-testid="hero-cta"
          >
            Let's Connect
          </a>
        </motion.div>
      </AnimatedSection>
    </section>
  );
};

// Section 2: Who I Am
const WhoIAmSection = () => {
  return (
    <section id="who-i-am" className="py-24 sm:py-32 px-6 sm:px-12" data-testid="who-i-am-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-2">
            <motion.div variants={fadeUp} className="accent-line mb-6 md:mb-0"></motion.div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <motion.h2 
              variants={fadeUp}
              className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-8"
            >
              Who I Am
            </motion.h2>
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl font-sans font-light leading-relaxed text-[#5C5C5C]"
            >
              I spent fifteen years inside large organizations — HSBC, Wells Fargo — watching leaders navigate complexity, ambiguity, and growth. In 2017, I stepped out to do something I couldn't do from the inside: listen more carefully, and share more freely. I am a student before I am a teacher. My foundation is Bhagawan Sri Sathya Sai Baba's teaching that knowledge finds its highest purpose when it flows outward — given without condition, received without obligation. That is what Sai Acuity is built on.
            </motion.p>
          </div>
        </div>
      </AnimatedSection>
    </section>
  );
};

// Section 3: What I Do
const WhatIDoSection = () => {
  const services = [
    {
      title: "Conversations",
      description: "I host The Monday Morning Learning Podcast — weekly conversations with leaders, thinkers, and practitioners that distill one insight worth carrying into the week.",
      linkText: "Visit themondaymorningpodcasts.in",
      linkUrl: "https://themondaymorninglearningpodcasts.in/",
      isExternal: true
    },
    {
      title: "Stories",
      description: "I compile books. Not write them — compile them. I take the lived experience of remarkable people and shape it into narratives that others can learn from. The Third Listening. The Inner Architecture. Runversations.",
      linkText: "Explore the books",
      linkUrl: "#books",
      isExternal: false
    },
    {
      title: "Systems",
      description: "I design frameworks and build emerging AI-led learning experiments that help leaders think more clearly — and keep thinking long after the conversation ends.",
      linkText: "Work with me",
      linkUrl: "mailto:ravi.jagarlapudi@saiacuity.com",
      isExternal: false
    }
  ];

  return (
    <section id="what-i-do" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="what-i-do-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          What I Do
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {services.map((service, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="service-card pt-8 border-t border-[#E5E0D8]"
              data-testid={`service-card-${index}`}
            >
              <h3 className="font-serif text-xl sm:text-2xl text-[#1C1C1C] mb-4">
                {service.title}
              </h3>
              <p className="text-base font-sans font-light text-[#5C5C5C] mb-6 leading-relaxed">
                {service.description}
              </p>
              <a 
                href={service.linkUrl}
                className="text-link text-sm font-sans text-[#1C1C1C]"
                target={service.isExternal ? "_blank" : undefined}
                rel={service.isExternal ? "noopener noreferrer" : undefined}
                data-testid={`service-link-${index}`}
              >
                {service.linkText}
                <span className="arrow">→</span>
              </a>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// Section 4: Books
const BooksSection = () => {
  const books = [
    {
      number: "01",
      title: "The Third Listening",
      description: "Season 1 — 18 leaders, distilled into one book of leadership insight.",
      linkText: "View on Amazon",
      linkUrl: "https://amzn.in/d/02jL0GjL",
      available: true
    },
    {
      number: "02",
      title: "The Inner Architecture",
      description: "Season 2 — 48 conversations, shaping into a book about how leaders are built from within.",
      linkText: "View on Amazon",
      linkUrl: "https://amzn.in/d/07XKnc57",
      available: true
    },
    {
      number: "03",
      title: "Runversations",
      description: "A book born from running conversations with one remarkable human and his world.",
      linkText: "Coming Soon",
      linkUrl: null,
      available: false
    }
  ];

  return (
    <section id="books" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="books-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          Stories I Have Shaped
        </motion.h2>
        
        <div className="space-y-0">
          {books.map((book, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className={`book-row py-8 border-b border-[#E5E0D8] grid grid-cols-12 gap-4 items-start ${book.available ? 'cursor-pointer' : ''}`}
              data-testid={`book-row-${index}`}
            >
              <div className="col-span-2 sm:col-span-1">
                <span className="text-sm font-sans text-[#5C5C5C]">{book.number}</span>
              </div>
              <div className="col-span-10 sm:col-span-11">
                <h3 className="font-serif text-xl sm:text-2xl text-[#1C1C1C] mb-2">
                  {book.title}
                </h3>
                <p className="text-base font-sans font-light text-[#5C5C5C] mb-4">
                  {book.description}
                </p>
                {book.available ? (
                  <a 
                    href={book.linkUrl}
                    className="text-link text-sm font-sans text-[#E68A00]"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`book-link-${index}`}
                  >
                    {book.linkText}
                    <span className="arrow">→</span>
                  </a>
                ) : (
                  <span className="text-sm font-sans text-[#9A9A9A]" data-testid={`book-link-${index}`}>
                    {book.linkText}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// Section 5: Currently Shaping
const CurrentlyShapingSection = () => {
  const projects = [
    "Runversations — a book in motion, expected 2026",
    "MMLP — weekly episodes, every Monday morning",
    "Emerging AI-led learning experiments in leadership development",
    "A leadership biography in progress — the story of an entrepreneur who built bridges between technology and rural India"
  ];

  return (
    <section id="currently-shaping" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="currently-shaping-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          What I Am Building Now
        </motion.h2>
        
        <div className="space-y-6">
          {projects.map((project, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="flex items-start gap-4"
              data-testid={`project-item-${index}`}
            >
              <span className="w-2 h-2 rounded-full bg-[#E68A00] mt-3 flex-shrink-0"></span>
              <p className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C]">
                {project}
              </p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// NEW Section: Conversational Learning Experiments
const ConversationalExperimentsSection = () => {
  return (
    <section id="experiments" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="experiments-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-2">
            <motion.div variants={fadeUp} className="accent-line mb-6 md:mb-0"></motion.div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <motion.h2 
              variants={fadeUp}
              className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-8"
            >
              Conversational Learning Experiments
            </motion.h2>
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl font-sans font-light leading-relaxed text-[#5C5C5C] mb-8"
            >
              We are currently exploring AI-assisted conversational books — interactive learning experiences where readers can engage with ideas, frameworks, and thinking patterns long after the final page.
            </motion.p>
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl font-serif font-light italic text-[#1C1C1C] mb-10"
            >
              The first experiment begins with From Intuition to Intention.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link 
                to="/from-intuition-to-intention"
                className="inline-block border border-[#E5E0D8] px-8 py-4 text-sm font-sans tracking-wide text-[#1C1C1C] hover:border-[#E68A00] hover:text-[#E68A00] transition-all"
                data-testid="experiments-cta"
              >
                Explore The Prototype
              </Link>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>
    </section>
  );
};

// Section 6: Voices (Testimonials)
const VoicesSection = () => {
  const testimonials = [
    {
      quote: "A powerhouse of positivity — he comes with clear intent and a clearer effort backing it up.",
      name: "Sanjiv Agarwal",
      title: "Global Enterprise Leader, Swiss Re",
      linkedin: "https://www.linkedin.com/in/sanjivagarwal/"
    },
    {
      quote: "He brings deep experiences and stories to the centre stage.",
      name: "Dr. Sujatha Muthanna",
      title: "Head of Learning Advisory, Infosys",
      linkedin: "https://www.linkedin.com/in/dr-sujatha-muthanna-547345b/"
    },
    {
      quote: "He brings a rare level of intention, creativity, and care to how he helps others grow.",
      name: "Sai Sravan Cherukuri",
      title: "Author & IT Program Manager",
      linkedin: "https://www.linkedin.com/in/sai-sravan-cherukuri-irs/"
    },
    {
      quote: "Passionate about how learning can truly make a difference — channeling all his energies into making that happen is his core strength.",
      name: "Shubhayu Sengupta",
      title: "Chief People Officer, Zeta",
      linkedin: "https://www.linkedin.com/in/shubhayu-sengupta-6b44887/"
    }
  ];

  return (
    <section id="voices" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="voices-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          Voices
        </motion.h2>
        
        <div className="space-y-12">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="border-l-2 border-[#E68A00] pl-6 sm:pl-8"
              data-testid={`testimonial-${index}`}
            >
              <p className="text-lg sm:text-xl font-serif font-light italic text-[#1C1C1C] leading-relaxed mb-4">
                "{testimonial.quote}"
              </p>
              <div>
                <a 
                  href={testimonial.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-sans font-medium text-[#1C1C1C] hover:text-[#E68A00] transition-colors underline-animation"
                  data-testid={`testimonial-link-${index}`}
                >
                  {testimonial.name}
                </a>
                <p className="text-sm font-sans text-[#5C5C5C]">
                  {testimonial.title}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// Section 7: Reach Me
const ReachMeSection = () => {
  return (
    <section id="reach-me" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="reach-me-section">
      <AnimatedSection className="max-w-5xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1C1C1C] mb-8"
        >
          Let's Begin With a Conversation
        </motion.h2>
        
        <motion.p 
          variants={fadeUp}
          className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] mb-12 max-w-3xl"
        >
          Giving is how I start every relationship. If something here resonated — a book idea, a conversation, or simply a thought worth exchanging — I'd love to hear from you.
        </motion.p>
        
        <motion.div variants={fadeUp} className="space-y-4 mb-12">
          <div className="flex items-center gap-4">
            <span className="text-sm font-sans uppercase tracking-widest text-[#5C5C5C] w-20">Email</span>
            <a 
              href="mailto:ravi.jagarlapudi@saiacuity.com" 
              className="underline-animation text-base sm:text-lg font-sans text-[#1C1C1C]"
              data-testid="contact-email-link"
            >
              ravi.jagarlapudi@saiacuity.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-sans uppercase tracking-widest text-[#5C5C5C] w-20">Phone</span>
            <a 
              href="tel:9885024104" 
              className="underline-animation text-base sm:text-lg font-sans text-[#1C1C1C]"
              data-testid="contact-phone-link"
            >
              9885024104
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-sans uppercase tracking-widest text-[#5C5C5C] w-20">LinkedIn</span>
            <a 
              href="https://www.linkedin.com/in/jagarlapudi-ravi-kanth-9b567113/"
              className="underline-animation text-base sm:text-lg font-sans text-[#1C1C1C]"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
            >
              Connect on LinkedIn
            </a>
          </div>
        </motion.div>
        
        <motion.div variants={fadeUp}>
          <a 
            href="mailto:ravi.jagarlapudi@saiacuity.com"
            className="btn-primary inline-block font-sans"
            data-testid="contact-cta"
          >
            Write to Me
          </a>
        </motion.div>
      </AnimatedSection>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="footer">
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-sm font-sans text-[#5C5C5C]">
          © 2026 Sai Acuity · Hyderabad, India · Enabling Learning Through Insight
        </p>
      </div>
    </footer>
  );
};

// Homepage Component
const HomePage = () => {
  useEffect(() => {
    // Smooth scroll for anchor links
    const handleClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleClick);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleClick);
      });
    };
  }, []);

  return (
    <div className="bg-[#F9F8F6] min-h-screen" data-testid="app-container">
      <Navigation />
      <main>
        <HeroSection />
        <WhoIAmSection />
        <WhatIDoSection />
        <BooksSection />
        <CurrentlyShapingSection />
        <ConversationalExperimentsSection />
        <VoicesSection />
        <ReachMeSection />
      </main>
      <Footer />
    </div>
  );
};

// ============================================================
// FROM INTUITION TO INTENTION PAGE
// ============================================================

// Navigation for the book page
const BookPageNavigation = () => {
  return (
    <nav className="nav-glass fixed top-0 left-0 right-0 z-50 border-b border-[#E5E0D8]" data-testid="book-navigation">
      <div className="max-w-6xl mx-auto px-6 sm:px-12 py-4 flex justify-between items-center">
        <Link 
          to="/" 
          className="font-serif text-lg text-[#1C1C1C] hover:text-[#E68A00] transition-colors"
          data-testid="book-nav-home"
        >
          Sai Acuity
        </Link>
        <div className="hidden sm:flex items-center gap-8">
          <Link to="/" className="text-sm tracking-wide text-[#5C5C5C] hover:text-[#1C1C1C] transition-colors" data-testid="book-nav-back">← Back to Main</Link>
        </div>
      </div>
    </nav>
  );
};

// Book Hero Section
const BookHeroSection = () => {
  return (
    <section className="min-h-[90vh] flex flex-col justify-center px-6 sm:px-12 pt-24 pb-16" data-testid="book-hero-section">
      <AnimatedSection className="max-w-4xl mx-auto w-full text-center">
        <motion.span 
          variants={fadeUp}
          className="text-xs font-sans uppercase tracking-[0.2em] text-[#E68A00] mb-8 block"
        >
          A Conversational Book Experience
        </motion.span>
        
        <motion.h1 
          variants={fadeUp}
          className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.15] text-[#1C1C1C] mb-10"
        >
          Some books end.<br />Some continue the conversation.
        </motion.h1>
        
        <motion.p 
          variants={fadeUp}
          className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] mb-12 max-w-3xl mx-auto leading-relaxed"
        >
          <span className="font-serif italic text-[#1C1C1C]">From Intuition to Intention</span> is an experimental conversational book experience built around the frameworks, reflections, and lived philosophy of Pankaj Rai.
        </motion.p>
        
        <motion.div variants={fadeUp} className="flex flex-col items-center gap-6">
          <button 
            onClick={() => {
              const element = document.getElementById('continue-conversation');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="btn-primary inline-block font-sans px-10 py-4"
            data-testid="book-hero-cta"
          >
            Talk To The Book
          </button>
          <span className="text-sm font-sans text-[#9A9A9A] italic">
            Conversational prototype currently in development.
          </span>
        </motion.div>
      </AnimatedSection>
    </section>
  );
};

// The Idea Section
const TheIdeaSection = () => {
  return (
    <section className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="book-idea-section">
      <AnimatedSection className="max-w-4xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-12"
        >
          The Book That Talks Back
        </motion.h2>
        
        <div className="space-y-6">
          <motion.p variants={fadeUp} className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] leading-relaxed">
            Most books are static experiences.
          </motion.p>
          
          <motion.div variants={fadeUp} className="py-6 pl-6 border-l-2 border-[#E5E0D8]">
            <p className="text-lg font-sans font-light text-[#5C5C5C] leading-relaxed">
              You read them.<br />
              Underline passages.<br />
              Carry a few ideas forward.<br />
              And eventually, the conversation fades.
            </p>
          </motion.div>
          
          <motion.p variants={fadeUp} className="text-lg sm:text-xl font-serif font-light italic text-[#1C1C1C] leading-relaxed pt-4">
            From Intuition to Intention explores a different possibility:<br />
            a book that responds.
          </motion.p>
          
          <motion.p variants={fadeUp} className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] leading-relaxed pt-4">
            Readers can ask questions, revisit frameworks, seek clarity, challenge assumptions, and continue exploring the thinking behind the work itself.
          </motion.p>
        </div>
      </AnimatedSection>
    </section>
  );
};

// Runversations Section
const RunversationsSection = () => {
  return (
    <section className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="book-runversations-section">
      <AnimatedSection className="max-w-4xl mx-auto">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-2">
            <motion.div variants={fadeUp} className="accent-line mb-6 md:mb-0"></motion.div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <motion.h2 
              variants={fadeUp}
              className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-8"
            >
              The Saturday Morning Conversations
            </motion.h2>
            
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] leading-relaxed mb-6"
            >
              Many of the ideas in this book emerged not in conference rooms, but during long Saturday morning runs and conversations — what Pankaj calls <span className="font-serif italic text-[#1C1C1C]">Runversations</span>.
            </motion.p>
            
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl font-sans font-light text-[#5C5C5C] leading-relaxed"
            >
              Over time, those conversations evolved into frameworks, reflections, and ways of thinking about work, leadership, family, learning, and freedom.
            </motion.p>
          </div>
        </div>
      </AnimatedSection>
    </section>
  );
};

// Sample Questions Section
const SampleQuestionsSection = () => {
  const questions = [
    "What is the Product Career?",
    "Explain the Zigzag Career simply.",
    "What does 'Full Stack Human' mean?",
    "How does the FREE framework work?",
    "Which framework applies during career transitions?"
  ];

  return (
    <section className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="book-questions-section">
      <AnimatedSection className="max-w-4xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          Questions You Might Ask
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {questions.map((question, index) => (
            <motion.div 
              key={index}
              variants={fadeUp}
              className="py-6 px-6 border border-[#E5E0D8] hover:border-[#E68A00] transition-colors"
              data-testid={`sample-question-${index}`}
            >
              <p className="text-base sm:text-lg font-sans font-light text-[#1C1C1C]">
                {question}
              </p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>
    </section>
  );
};

// About The Book Section
const AboutBookSection = () => {
  return (
    <section className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="book-about-section">
      <AnimatedSection className="max-w-4xl mx-auto text-center">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-16"
        >
          About The Book
        </motion.h2>
        
        <motion.div 
          variants={fadeUp}
          className="mx-auto mb-12 max-w-xs sm:max-w-sm"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_sai-acuity/artifacts/z70gdmkt_Pankaj%20Rai%20From%20Intuition%20to%20Intention%20Book%20Cover.png"
            alt="From Intuition to Intention by Pankaj Rai - Book Cover"
            className="w-full h-auto shadow-[0_8px_30px_rgba(0,0,0,0.12)] rounded-sm"
            data-testid="book-cover-image"
          />
        </motion.div>
        
        <motion.h3 
          variants={fadeUp}
          className="font-serif text-2xl font-light italic text-[#1C1C1C] mb-4"
        >
          From Intuition to Intention
        </motion.h3>
        
        <motion.p 
          variants={fadeUp}
          className="text-base font-sans text-[#5C5C5C]"
        >
          Coming Soon · Sai Acuity · 2026
        </motion.p>
      </AnimatedSection>
    </section>
  );
};

// Conversational Interaction Section
const ConversationalSection = () => {
  const [question, setQuestion] = useState("");
  const [conversation, setConversation] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || isThinking) return;

    const userQuestion = question.trim();
    setQuestion("");
    setIsThinking(true);
    setError(null);

    // Add the question to conversation
    setConversation(prev => [...prev, { type: "question", text: userQuestion }]);

    try {
      const response = await fetch(`${BACKEND_URL}/api/conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userQuestion,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 503) {
          throw new Error('service_unavailable');
        }
        throw new Error(errorData.detail || 'Failed to get response');
      }

      const data = await response.json();
      
      // Store session ID for conversation continuity
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      setConversation(prev => [...prev, { type: "response", text: data.response }]);
    } catch (err) {
      console.error('Conversation error:', err);
      if (err.message === 'service_unavailable') {
        setError('The conversational experience is temporarily paused. We are working to restore it soon.');
      } else {
        setError('Unable to continue the conversation. Please try again.');
      }
      // Remove the question if there was an error
      setConversation(prev => prev.slice(0, -1));
    } finally {
      setIsThinking(false);
    }
  };

  return (
    <section id="continue-conversation" className="py-24 sm:py-32 px-6 sm:px-12 border-t border-[#E5E0D8] scroll-mt-20" data-testid="book-conversation-section">
      <AnimatedSection className="max-w-3xl mx-auto">
        <motion.h2 
          variants={fadeUp}
          className="font-serif text-3xl sm:text-4xl font-light text-[#1C1C1C] mb-6 text-center"
        >
          Continue The Conversation
        </motion.h2>
        
        <motion.p 
          variants={fadeUp}
          className="text-base font-sans font-light text-[#5C5C5C] mb-16 text-center max-w-2xl mx-auto"
        >
          Explore the ideas, frameworks, and reflections within the book.
        </motion.p>

        {/* Error Message */}
        {error && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8 p-4 border border-[#E68A00] text-center"
          >
            <p className="text-sm font-sans text-[#E68A00]">{error}</p>
          </motion.div>
        )}

        {/* Conversation History */}
        <AnimatePresence>
          {conversation.length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-16 space-y-12"
            >
              {conversation.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className={item.type === "question" ? "text-right" : ""}
                >
                  {item.type === "question" ? (
                    <div className="inline-block text-left max-w-[85%]">
                      <span className="text-xs font-sans uppercase tracking-widest text-[#9A9A9A] mb-3 block">
                        Your Question
                      </span>
                      <p className="text-lg font-serif italic text-[#1C1C1C]">
                        "{item.text}"
                      </p>
                    </div>
                  ) : (
                    <div className="border-l-2 border-[#E68A00] pl-8">
                      <span className="text-xs font-sans uppercase tracking-widest text-[#E68A00] mb-4 block">
                        A Reflection
                      </span>
                      <p className="text-lg sm:text-xl font-serif font-light text-[#1C1C1C] leading-relaxed whitespace-pre-wrap">
                        {item.text}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
              
              {/* Thinking indicator */}
              {isThinking && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-l-2 border-[#E5E0D8] pl-8"
                >
                  <span className="text-xs font-sans uppercase tracking-widest text-[#9A9A9A] mb-4 block">
                    Reflecting...
                  </span>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#E5E0D8] rounded-full animate-pulse"></span>
                    <span className="w-2 h-2 bg-[#E5E0D8] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                    <span className="w-2 h-2 bg-[#E5E0D8] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></span>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <motion.form 
          variants={fadeUp}
          onSubmit={handleSubmit}
          className="border border-[#E5E0D8] p-8 sm:p-12"
        >
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question about the ideas, frameworks, or reflections in the book…"
            className="w-full bg-transparent border-none outline-none resize-none font-serif text-lg text-[#1C1C1C] placeholder-[#9A9A9A] leading-relaxed min-h-[120px]"
            data-testid="conversation-input"
          />
          
          <div className="flex justify-end mt-8 pt-8 border-t border-[#E5E0D8]">
            <button
              type="submit"
              disabled={!question.trim() || isThinking}
              className="font-sans text-sm tracking-widest uppercase text-[#1C1C1C] hover:text-[#E68A00] transition-colors disabled:text-[#9A9A9A] disabled:cursor-not-allowed"
              data-testid="conversation-submit"
            >
              {isThinking ? 'Reflecting...' : 'Continue The Conversation →'}
            </button>
          </div>
        </motion.form>

        {/* Subtle note */}
        <motion.p 
          variants={fadeUp}
          className="text-center text-sm font-sans text-[#9A9A9A] mt-8 italic"
        >
          This is an early prototype exploring conversational books.
        </motion.p>
      </AnimatedSection>
    </section>
  );
};

// Book Footer
const BookFooter = () => {
  return (
    <footer className="py-16 px-6 sm:px-12 border-t border-[#E5E0D8]" data-testid="book-footer">
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-serif text-xl italic text-[#5C5C5C] mb-8">
          "The conversation continues beyond the final page."
        </p>
        <p className="text-sm font-sans text-[#9A9A9A]">
          © 2026 Sai Acuity · An Experiment in Conversational Learning
        </p>
      </div>
    </footer>
  );
};

// From Intuition to Intention Page
const FromIntuitionToIntentionPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F9F8F6] min-h-screen" data-testid="book-page-container">
      <BookPageNavigation />
      <main>
        <BookHeroSection />
        <TheIdeaSection />
        <RunversationsSection />
        <SampleQuestionsSection />
        <AboutBookSection />
        <ConversationalSection />
      </main>
      <BookFooter />
    </div>
  );
};

// Main App with Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/from-intuition-to-intention" element={<FromIntuitionToIntentionPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
